// Appadmin.tsx – Mantine version

import { Box, Button, Card, Checkbox, Group, Stack, Text, TextInput, Textarea, Title } from "@mantine/core";
import React, { useState } from "react";

import { IChecklistTemplate, IChecklistTemplateCategory } from "@mctypes/checklist.types";
import { setShowSuccessMessageThunk } from "../../../global/globalSlice";
import { useAppDispatch } from "../../../hooks";
import { doTestEmail, doTestReceiveEmail } from "../admin/restQueries";
import { doUploadChecklistJsonTemplate } from "../checklist/checklistAPI";
import { ActiveBatchJob } from "./ActiveBatchJob";
import { AddProjectToMcApps } from "./AddProjectToMcApps";
import { AddUserToProject } from "./AddUserToProject";
import { EmailTemplatesAdmin } from "./EmailTemplatesAdmin";
import { IntegrationsHealthChecker } from "./IntegrationsHealthChecker";
import McBimMasterAdmin from "./McBimMasterAdmin";
import { McChecklistDocxTemplatesAdmin } from "./McChecklistDocxTemplatesAdmin";
import { OfficialIssueTemplateUpload } from "./OfficialIssueTemplateUpload";
import { OfficialMeetingTemplateUpload } from "./OfficialMeetingTemplateUpload";
import { ServiceAccounts } from "./ServiceAccounts";
import { ValidateOtherAttributesBoardAdmin } from "./ValidateOtherAttributesBoardAdmin";

const Appadmin: React.FC = () => {
  const sendEmail = async (template: string) => {
    try {
      if (template === "receive-email") {
        return await doTestReceiveEmail();
      }
      await doTestEmail(template);
    } catch (error: any) {
      alert(JSON.stringify(error));
    }
  };

  const emailtemplates = ["reset-password", "receive-email", "task-notify", "project-invite", "meeting-agenda"];

  return (
    <Box h="100%" p="sm" style={{ overflow: "auto", background: "var(--mantine-color-gray-1)" }}>
      <Stack gap="md">
        <McChecklistDocxTemplatesAdmin />
        <EmailTemplatesAdmin />

        <Card withBorder shadow="sm" p="md">
          <Stack gap="sm">
            <Title order={3}>E-post-tester</Title>
            <Group gap="xs" wrap="wrap">
              {emailtemplates.map((template) => (
                <Button key={template} onClick={() => sendEmail(template)}>
                  {template}
                </Button>
              ))}
            </Group>
          </Stack>
        </Card>
        <IntegrationsHealthChecker />

        <Card withBorder shadow="sm" p="md">
          <Stack gap="xs">
            <Title order={3}>Offisiell mal for møteapp</Title>
            <OfficialMeetingTemplateUpload />
          </Stack>
        </Card>

        <Card withBorder shadow="sm" p="md">
          <Stack gap="xs">
            <Title order={3}>Offisiell mal for sakshåndtering</Title>
            <OfficialIssueTemplateUpload />
          </Stack>
        </Card>

        <Card withBorder shadow="sm" p="md">
          <Stack gap="xs">
            <Title order={3}>Batchjobb – funksjonstoggle</Title>
            <ActiveBatchJob />
          </Stack>
        </Card>

        <Card withBorder shadow="sm" p="md">
          <Stack gap="xs">
            <Title order={3}>McBIM</Title>
            <McBimMasterAdmin />
          </Stack>
        </Card>

        <Card withBorder shadow="sm" p="md">
          <Stack gap="xs">
            <Title order={3}>Legg til mal for sjekkliste</Title>
            <UploadChecklistTemplate />
          </Stack>
        </Card>

        <Card withBorder shadow="sm" p="md">
          <Stack gap="xs">
            <Title order={3}>Legg til bruker i prosjekt</Title>
            <AddUserToProject />
          </Stack>
        </Card>

        <Card withBorder shadow="sm" p="md">
          <Stack gap="xs">
            <Title order={3}>Legg til prosjekt i MCApps</Title>
            <AddProjectToMcApps />
          </Stack>
        </Card>

        <Card withBorder shadow="sm" p="md">
          <Stack gap="xs">
            <Title order={3}>Legg til tjenestekonto</Title>
            <ServiceAccounts />
          </Stack>
        </Card>

        <ValidateOtherAttributesBoardAdmin />
      </Stack>
    </Box>
  );
};

export default Appadmin;

const UploadChecklistTemplate: React.FC = () => {
  const [autoDownload, setAutoDownload] = useState<boolean>(true);
  const [aboutText, setAboutText] = useState<string>("");
  const [jsonText, setJsonText] = useState<string>("");
  const [csvText, setCsvText] = useState<string>("");
  const [tagsText, setTagsText] = useState<string>("");
  const [titleText, setTitleText] = useState<string>("");
  const [statusMessage, setStatusMessage] = useState<string>("");
  const dispatch = useAppDispatch();

  const saveNew = async () => {
    try {
      const template: IChecklistTemplate = JSON.parse(jsonText);
      await doUploadChecklistJsonTemplate(JSON.stringify(template));
      dispatch(setShowSuccessMessageThunk("Sjekklistemalen ble lagret!"));
    } catch (error: any) {
      setStatusMessage(error?.message || String(error));
      return;
    }
  };

  const parseCsvText = () => {
    const template: IChecklistTemplate = {
      aboutText: aboutText.trim(),
      categories: [],
      tags: tagsText
        .split(",")
        .map((x) => x.trim())
        .filter((x) => x !== ""),
      templateType: "MC",
      name: titleText,
    };

    const lines = csvText.trim().split("\n");
    const templateRows = new Map<string, [string, string][]>();
    let currentLine = "";

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const nextLineHasId = i + 1 < lines.length ? /^(\d+\.\d+|\d+)\t/.test(lines[i + 1]) : false;

      currentLine += line;

      if (nextLineHasId === false && i + 1 < lines.length) {
        currentLine += "\n";
        continue;
      }

      const rowParts = currentLine.trim().match(/(\d+\.\d+|\d+)\t((?:.|\n)*)/);
      currentLine = "";

      if (rowParts == null || rowParts.length !== 3) {
        console.error(`Ukjent radformat på rad ${i}: "${lines[i]}"`);
        return;
      }

      const id = rowParts[1].trim();
      const text = rowParts[2].trim();
      const groupId = id.split(".")[0];

      const catRows = templateRows.get(groupId);
      if (catRows == null) {
        templateRows.set(groupId, [[id, text]]);
      } else {
        catRows.push([id, text]);
      }
    }

    templateRows.forEach((rows) => {
      for (let i = 0; i < rows.length; i++) {
        const cat: IChecklistTemplateCategory = {
          title: rows[i][1],
          items: [],
        };

        while (i < rows.length - 1) {
          i += 1;
          if (/^\d+$/.test(rows[i][0]) === false) {
            cat.items.push({ title: rows[i][1] });
          } else {
            i -= 1;
            break;
          }
        }

        template.categories.push(cat);
      }
    });

    const _json = JSON.stringify(template, null, 2);
    setJsonText(_json);

    if (autoDownload) {
      download(titleText + ".json", _json);
    }
  };

  const download = (file: string, text: string) => {
    const element = document.createElement("a");
    element.setAttribute("href", "data:application/json;charset=utf-8," + encodeURIComponent(text));
    element.setAttribute("download", file);
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  return (
    <Stack gap="sm">
      <TextInput
        label="Tittel"
        placeholder="Navn på mal"
        value={titleText}
        onChange={(e) => setTitleText(e.currentTarget.value)}
      />

      <TextInput
        label="Etiketter (kommadelt)"
        placeholder="f.eks. elektro, vvs, brann"
        value={tagsText}
        onChange={(e) => setTagsText(e.currentTarget.value)}
      />

      <Textarea
        label="Om tekst"
        minRows={4}
        autosize
        value={aboutText}
        onChange={(e) => setAboutText(e.currentTarget.value)}
      />

      <Textarea
        label="CSV"
        description="Lim inn CSV-innhold (ID og tekst separert med tab)"
        minRows={6}
        autosize
        value={csvText}
        onChange={(e) => setCsvText(e.currentTarget.value)}
      />

      <Group gap="sm" align="center">
        <Button disabled={csvText.trim() === ""} onClick={parseCsvText}>
          Konverter til JSON
        </Button>
        <Checkbox
          label="Lagre JSON til disk"
          checked={autoDownload}
          onChange={(e) => setAutoDownload(e.currentTarget.checked)}
        />
      </Group>

      <Textarea
        label="JSON"
        minRows={6}
        autosize
        value={jsonText}
        onChange={(e) => setJsonText(e.currentTarget.value)}
      />

      {statusMessage && <Text c="red">{statusMessage}</Text>}

      <Button variant="filled" disabled={jsonText.trim() === ""} onClick={saveNew}>
        Lagre mal
      </Button>
    </Stack>
  );
};
